import { ORG_PERMISSION_IMPLICATIONS } from "./org-permissions.js";
import { isReservedOrgPermissionKey } from "./permission-key.js";
//#region src/permissions.ts
/** Core site-level permissions — plain string constants. */
const CorePermission = {
	ReleasesManage: "releases:manage",
	ChannelsManage: "channels:manage",
	AuditView: "audit:view",
	AppsManage: "apps:manage",
	OrgsEnroll: "orgs:enroll",
	DomainsManage: "domains:manage"
};
const WILDCARD_PERMISSION = "*";
/**
* Maps org roles to their default permission sets.
* core-api seeds these at org creation; orgs can customize via role management UI.
*/
const ORG_ROLE_PERMISSIONS = {
	owner: ["*"],
	admin: [
		"org:read",
		"org:manage",
		"users:read",
		"users:manage",
		"groups:read",
		"groups:manage",
		"settings:read",
		"settings:manage",
		"channels:read",
		"channels:manage",
		"fixtures:access",
		"audit:read",
		"org:llm-credentials:manage",
		"platform:machine-principal:enroll",
		"platform:machine-principal:revoke",
		"platform:delegation:mint"
	],
	member: [
		"org:read",
		"users:read",
		"groups:read",
		"fixtures:access"
	]
};
/** Resolve permissions for an org role. Pure function. */
function resolveOrgRolePermissions(role) {
	return ORG_ROLE_PERMISSIONS[role] ?? [];
}
/**
* Check if a permission set grants a specific permission.
*
* Wildcard semantics:
*   - The literal `*` matches every permission.
*   - A trailing `:*` is a prefix wildcard: `cp:*` matches `cp:dp:cancel`,
*     `cp:app:*` matches `cp:app:publish` etc.
*
* The prefix-wildcard fix is the latent-bug fix called out by Plan 23 PR0a:
* the previous implementation only honored the bare `*`, so a role granted
* `cp:*` did not pass `cp:dp:cancel` checks even though semantically it should.
*
* Umbrella semantics (#1130):
*   - A held key listed in {@link ORG_PERMISSION_IMPLICATIONS} also satisfies
*     every key it implies — one level, never chased transitively. This is what
*     keeps a pre-split `settings:manage` grant (a stored role permission, an
*     OAuth client's `allowedScopes`, an already-issued delegated token)
*     authorizing the routes that moved onto the four narrower
*     `settings:{roles,identity}:{read,manage}` keys. See that constant's
*     docblock for why the expansion cannot live at token-mint time.
*
* ⚠️ Two call sites deliberately do NOT want umbrella/wildcard semantics and use
* exact membership instead — `admin/user.service.ts:145` (an `scp` list, which
* is wildcard-free by construction) and the app-permission leg of
* {@link buildShellPermissions}. Do not "unify" them onto this function.
*/
function hasPermission(resolved, required) {
	const list = resolved instanceof Set ? resolved : resolved;
	for (const p of list) {
		if (p === "*") return true;
		if (p === required) return true;
		if (p.endsWith(":*")) {
			const prefix = p.slice(0, -1);
			if (required.startsWith(prefix)) return true;
		}
		if (ORG_PERMISSION_IMPLICATIONS.get(p)?.has(required) === true) return true;
	}
	return false;
}
/**
* The permission keys of a token's `appPermissions` tuples that name an app
* (#2883), in claim order.
*
* An entry counts only when it is an object with a string `permissionKey` and
* a non-empty `appId` or `appSlug`. Anything else is skipped: a bare string
* (an old flat form, which the Rust contracts decode as a claim with no app),
* `null`, or an object with no app identity. Every real tuple core-api mints
* carries `appId`; the catalog-miss sentinel carries `appSlug: ''` beside it.
* The value is read as `unknown` because a decoded token is untrusted input.
*
* @param appPermissions the token's `appPermissions` claim, in any shape
* @returns the keys of the entries that are app-identified tuples
*/
function appPermissionKeysOf(appPermissions) {
	if (!Array.isArray(appPermissions)) return [];
	const keys = [];
	for (const entry of appPermissions) {
		if (typeof entry !== "object" || entry === null) continue;
		const { permissionKey, appId, appSlug } = entry;
		if (typeof permissionKey === "string" && (typeof appId === "string" && appId !== "" || typeof appSlug === "string" && appSlug !== "")) keys.push(permissionKey);
	}
	return keys;
}
/**
* Whether a principal holds `required` on the org plane, reading both places
* a DP token can carry an app-granted key (#2883).
*
* Today core-api copies every app key an `explicit` principal is granted into
* the flat `orgPermissions` claim, and the same keys ride the typed
* `appPermissions` tuples. `OrgPermissionGuard`, `PermissionChecker` and the
* other org-plane checks read the flat list alone, so an app that guards its
* own key with `@RequireOrgPermission('<app-key>')` depends on that copy. This
* reads the tuple keys as a second source so the copy can be retired later
* without refusing those apps. Take the keys from {@link appPermissionKeysOf},
* which drops entries that do not name an app.
*
* It only widens. It admits more only where a token carries a tuple key its
* flat list lacks. Among core-api's mints that is the app-bound OAuth token,
* which writes `orgPermissions: []` beside its tuples; `AppPermissionGuard`
* already admits that token for the same key. Everywhere else the tuple keys
* are already in the flat list. The flat arm keeps its wildcard and
* implication matching through {@link hasPermission}. The tuple arm is an
* exact key match, because a tuple carries one declared key and never a
* wildcard. Which app a tuple names is not consulted, matching the flat copy,
* which does not record an app either.
*
* ⛔ A RESERVED KEY NEVER COUNTS FROM A TUPLE. The resolver filters reserved
* org and platform keys ({@link isReservedOrgPermissionKey}) out of the flat
* copy, so a grant row written before the grant-time gate cannot reach an
* org-plane check (S-F1). The tuples are not filtered that way, so this arm
* applies the same filter; otherwise an old `users:manage` app grant would
* pass `@RequireOrgPermission('users:manage')` through its tuple.
*
* @param orgPermissions the principal's flat `orgPermissions`, or `undefined`
* @param appPermissionKeys the keys of its app-identified tuples, or `undefined`
* @param required the key a route or check requires
* @returns true when either source holds `required`
*/
function holdsOrgPlanePermission(orgPermissions, appPermissionKeys, required) {
	if (hasPermission(orgPermissions ?? [], required)) return true;
	if (isReservedOrgPermissionKey(required)) return false;
	return (appPermissionKeys ?? []).includes(required);
}
/**
* Intersect a principal's resolved permissions with a delegated-token
* scope list (AD-4 — the single implementation behind the delegated mint
* and BOTH verifying guards' defense-in-depth re-intersection).
*
* Filter DIRECTION matters: we keep each requested scope `s` for which
* `hasPermission(perms, s)` holds — not the other way around — so a
* wildcard-holder (`*` or `prefix:*` in `perms`) is correctly narrowed to
* exactly the concrete scopes the token was delegated. Filtering `perms`
* by membership in `scp` would drop wildcard grants entirely (the literal
* `*` is never a scope — wildcards are forbidden in `scp`).
*
* An empty `scp` therefore yields zero permissions (fail-closed).
*/
function narrowPermissionsToScopes(perms, scp) {
	return scp.filter((s) => hasPermission(perms, s));
}
/**
* Build the fixture-facing `ShellPermissions` object the platform shell
* passes to hosted apps via `ShellHostProps`. Canonical home for this
* constructor: it lives next to `hasPermission` so every consumer (the
* shell, hosted apps, tests) gets identical, wildcard-aware semantics.
*
* ## App-scoped grants (#670, Seam A / D3)
*
* The DP JWT carries app-scoped grants (e.g. `perf:write`) in a SEPARATE
* `appPermissions[]` tuple claim, deliberately kept OUT of flat
* `orgPermissions` (resolver DEC-11 / D6). The shell passes the ACTIVE
* app's permission keys as `appPermissions` so those grants can authorize
* hosted-app UI actions:
*
*   - `hasPermission(p)` — the general, app-facing gate hosted apps call —
*     is true when the wildcard-aware `hasPermission(orgPermissions, p)`
*     matches (so a `*`/`prefix:*` owner passes ANY key), OR when `p` is an
*     EXACT member of the passed active-app keys (an explicit app grant).
*   - `hasOrgPermission(p)` stays ORG-scoped — it consults `orgPermissions`
*     ONLY (an app key is a different namespace). So the two predicates
*     diverge exactly when `p` is an app-only key, and are identical for
*     every org permission.
*
* `appPermissions` MUST already be scoped by the caller (the shell) to the
* active app's slug — this function receives only that app's keys and never
* re-scopes (security-audit §3: app A cannot read app B's grants). App keys
* are matched by exact membership, never expanded as wildcards (D3:
* explicit grants match by exact key).
*
* `hasOrgPermission` / `hasPermission` delegate to `hasPermission()` above,
* so a grant of `*` or a `prefix:*` wildcard matches as expected — the same
* semantics the SDK's `useHasPermission()` hook uses. (The shell's previous
* hand-rolled builder used an exact `Array.includes`, which silently failed
* wildcard grants.)
*
* App keys are NOT unioned into the returned `orgPermissions` array
* (DEC-11 / D6 stays); they are consulted only inside `hasPermission`.
*
* Role-derived convenience predicates (`canManage*`) key off `admin`/`owner`
* org roles; `sitePermissions` is empty on the data plane (no site-level
* permissions in that surface).
*
* @param orgRoles       The principal's org roles (e.g. `['owner']`).
* @param orgPermissions The flat, wildcard-aware org permission claim.
* @param appPermissions The ACTIVE app's permission keys (pre-scoped to the
*                       active app's slug by the caller). Optional; defaults
*                       to `[]` so 2-arg callers are unaffected.
*/
function buildShellPermissions(orgRoles, orgPermissions, appPermissions = []) {
	const isAdmin = () => orgRoles.includes("admin") || orgRoles.includes("owner");
	const isOwner = () => orgRoles.includes("owner");
	const hasOrg = (p) => hasPermission(orgPermissions, p);
	const appPermSet = new Set(appPermissions);
	const has = (p) => hasOrg(p) || appPermSet.has(p);
	return {
		orgPermissions: [...orgPermissions],
		sitePermissions: /* @__PURE__ */ new Set(),
		hasOrgPermission: hasOrg,
		isOrgAdmin: isAdmin,
		isOrgOwner: isOwner,
		hasPermission: has,
		canManageReleases: isAdmin,
		canManageChannels: isAdmin,
		canManageApps: isAdmin,
		canViewAuditTrail: isAdmin,
		canManageMembers: isAdmin,
		canManageContent: isAdmin,
		canManageOrgSettings: isOwner
	};
}
//#endregion
export { CorePermission, ORG_ROLE_PERMISSIONS, WILDCARD_PERMISSION, appPermissionKeysOf, buildShellPermissions, hasPermission, holdsOrgPlanePermission, narrowPermissionsToScopes, resolveOrgRolePermissions };

//# sourceMappingURL=permissions.js.map